import { Modal } from '@@/modals/Modal';
import { Button } from '@@/buttons';
import { JsonTree } from '@@/JsonTree';

import { LogEntry } from './types';
import { SeverityPill } from './SeverityPill';
import { formatTimeLocal, tryParseJSON } from './utils';

interface Props {
  log: LogEntry;
  onDismiss: () => void;
}

const LABEL_CLASS =
  'mb-0.5 text-xs font-semibold uppercase tracking-wider text-gray-7 th-dark:text-gray-4 th-highcontrast:text-gray-4';

export function LogDetailModal({ log, onDismiss }: Props) {
  const parsedMessage = tryParseJSON(log.message);
  const labelEntries = log.labels ? Object.entries(log.labels) : [];
  return (
    <Modal onDismiss={onDismiss} size="lg" aria-label="Log details">
      <Modal.Header title="Log details" />
      <Modal.Body>
        <dl className="flex flex-col gap-3 text-sm">
          {log.time && (
            <div>
              <dt className={LABEL_CLASS}>Time</dt>
              <dd className="font-mono">{formatTimeLocal(log.time)}</dd>
            </div>
          )}
          {log.source && (
            <div>
              <dt className={LABEL_CLASS}>Source</dt>
              <dd className="text-blue-8 th-highcontrast:text-blue-3 th-dark:text-blue-4">
                {log.source}
              </dd>
            </div>
          )}
          {log.severity && (
            <div>
              <dt className={LABEL_CLASS}>Severity</dt>
              <dd>
                <SeverityPill severity={log.severity} />
              </dd>
            </div>
          )}
          <div>
            <dt className={LABEL_CLASS}>Message</dt>
            <dd>
              {parsedMessage !== null ? (
                <div className="max-h-96 overflow-auto rounded-sm bg-gray-2 p-3 th-highcontrast:bg-black th-dark:bg-gray-9">
                  <JsonTree data={parsedMessage} />
                </div>
              ) : (
                <pre className="max-h-96 overflow-auto rounded-sm bg-gray-2 p-3 font-mono text-xs break-all whitespace-pre-wrap th-highcontrast:bg-black th-dark:bg-gray-9">
                  {log.message}
                </pre>
              )}
            </dd>
          </div>
          {labelEntries.length > 0 && (
            <div>
              <dt className={LABEL_CLASS}>Labels</dt>
              <dd>
                <div className="overflow-auto rounded-sm bg-gray-2 p-3 th-highcontrast:bg-black th-dark:bg-gray-9">
                  <dl className="flex flex-col gap-1">
                    {labelEntries.map(([key, value]) => (
                      <div key={key} className="flex gap-2 font-mono text-xs">
                        <span className="shrink-0 text-gray-7 th-highcontrast:text-gray-4 th-dark:text-gray-4">
                          {key}
                        </span>
                        <span className="break-all">{value}</span>
                      </div>
                    ))}
                  </dl>
                </div>
              </dd>
            </div>
          )}
        </dl>
      </Modal.Body>
      <Modal.Footer>
        <Button
          color="default"
          onClick={onDismiss}
          data-cy="log-detail-modal-close"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
